/**
 * GET /v1/me/tenants — the workspaces this login may open.
 *
 * The missing half of F-024. The tenant switcher and its store were built in
 * Phase 10 and have been dead ever since: the store's own comment says
 * "whichever route loader fetches the user's tenant list calls it after auth",
 * and no loader ever did, because the endpoint did not exist. The switcher
 * self-hides whenever the list has one or fewer entries, so a permanently empty
 * list rendered as a permanently invisible control.
 *
 * This is that fetch. See docs-vault/10-identity/.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { ApiError } from './client';

export interface MyTenant {
  tenantId: string;
  tenantName: string | null;
  role: string;
  /** True for the workspace the current token is scoped to. */
  current: boolean;
}

/**
 * A workspace someone has been INVITED to but has not accepted (2026-08-25).
 *
 * Kept out of `tenants` on purpose: these are not switchable, and a client that
 * has never heard of invitations must keep behaving exactly as it did.
 */
export interface PendingWorkspaceInvite {
  tenantId: string;
  tenantName: string | null;
  role: string;
  invitedAt: string;
}

export interface MyTenantsResponse {
  tenants: MyTenant[];
  /** Absent on responses from a backend older than 2026-08-25. */
  pendingInvites?: PendingWorkspaceInvite[];
  correlationId?: string;
}

export interface RespondToInviteRequest {
  tenantId: string;
  decision: 'accept' | 'decline';
}

export interface RespondToInviteResponse {
  tenantId: string;
  tenantName?: string;
  role: string;
  status: 'active' | 'revoked';
  /** Always false: accepting ADDS a workspace, it does not move you into it. */
  activeWorkspaceChanged: boolean;
  correlationId?: string;
}

/**
 * Poll-free read of this login's memberships.
 *
 * Deliberately tolerant: a failure leaves the switcher hidden rather than
 * blowing up the header, because this drives an optional convenience control.
 *
 * ⚠️ BUT TOLERANT IS NOT THE SAME AS SILENT, and the original `retry: false`
 * made those indistinguishable. The switcher hides at one-or-fewer workspaces,
 * so a single dropped request rendered EXACTLY like "you only have one
 * workspace" — a user who genuinely holds three would simply lose the ability
 * to reach the other two, with nothing anywhere saying why.
 *
 * So: retry transient failures (network / 5xx) twice, never retry a 4xx (a 401
 * or 403 will not change on a second attempt), and let callers read `isError`
 * to tell "no other workspaces" apart from "we could not find out".
 */
export function useMyTenants(enabled = true) {
  return useQuery<MyTenantsResponse, ApiError>({
    queryKey: ['me', 'tenants'],
    queryFn: () => api<MyTenantsResponse>('/api/v1/me/tenants'),
    enabled,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Accept or decline an invitation to a second workspace.
 *
 * On success both the tenant list and the invite list are refetched: accepting
 * moves a row from one to the other, and a stale switcher that still hides the
 * new workspace is the exact failure this endpoint exists to end.
 */
export function useRespondToWorkspaceInvite() {
  const qc = useQueryClient();
  return useMutation<RespondToInviteResponse, ApiError, RespondToInviteRequest>({
    mutationFn: (body) =>
      api<RespondToInviteResponse>('/api/v1/auth/workspace-invites/respond', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'tenants'] });
    },
  });
}
